import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Facebook } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthModal({ open, onOpenChange, onSuccess }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  let submitLabel;
  if (busy) submitLabel = "Please wait...";
  else if (mode === "login") submitLabel = "Sign In";
  else submitLabel = "Create Account";

  const facebookLogin = () => {
    const next = window.location.pathname + window.location.search;
    window.location.href = `${BACKEND_URL}/api/auth/facebook/login?next=${encodeURIComponent(next)}`;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {mode === "login" ? "Sign in to DealShed" : "Create your account"}
          </DialogTitle>
          <DialogDescription>
            {mode === "login" ? "Pop back in to see your ads, saved items and messages." : "Join DealShed and start buying and selling around Ireland."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Button type="button" onClick={facebookLogin} data-testid="auth-facebook-button"
            className="w-full bg-[#1877F2] hover:bg-[#1877F2]/90 text-white">
            <Facebook className="mr-2 h-4 w-4" /> Continue with Facebook
          </Button>
          <Button type="button" onClick={googleLogin} data-testid="auth-google-button" variant="outline"
            className="w-full border-slate-300 text-slate-700 hover:bg-slate-50">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="mr-2 h-4 w-4" />
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" />
            or use email
            <span className="h-px flex-1 bg-slate-200" />
          </div>
          {mode === "register" && (
            <div>
              <Label htmlFor="auth-name">Full name</Label>
              <Input id="auth-name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1" />
            </div>
          )}
          <div>
            <Label htmlFor="auth-email">Email</Label>
            <Input id="auth-email" type="email" data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
          </div>
          <div>
            <Label htmlFor="auth-password">Password</Label>
            <Input id="auth-password" type="password" data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="auth-submit-button" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
            {submitLabel}
          </Button>
          <p className="text-sm text-center text-slate-500">
            {mode === "login" ? "New to DealShed?" : "Already have an account?"}{" "}
            <button type="button" data-testid="auth-toggle-mode" className="text-emerald-600 font-medium hover:underline"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
